import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Icon, Separator } from '~/components';
import { Button } from '~/components/Button';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { APIError } from '../../api/errors';
import {
  getFixesHistory,
  getLabelPerfReport,
  getLables,
  getMonthlyContrib,
  getTopTenTracks,
} from '../../api/label-perf';
import { getLabelHierarchy, getLabels, getWMG1Labels } from '../../api/labels';
import type { AppliedLabelFilter } from '../../components/AppliedFilter/AppliedFilterList';
import { AppliedFilterList } from '../../components/AppliedFilter/AppliedFilterList';
import { Header } from '../../components/Header';
import {
  getAllChildLabelIds,
  getAllParentLabelIds,
} from '../../components/LabelFilter/helper';
import { LabelFilter } from '../../components/LabelFilter/LabelFilter';
import {
  CHART_TABS,
  DEFAULT_CHART_RANGE,
  DEFAULT_LABELS_FILTERS,
  DEFAULT_TOP_TEN_TAB,
} from '../../constants/label-perf';
import { useAmplifyUser } from '../../context/useAmplifyUser';
import type { LabelData, LabelHierarchy } from '../../types';
import {
  type FixesHistValue,
  type HistRange,
  type LabelPerfFilters,
  type LabelPerfModel,
  type MonthlyContrib,
  TopTenCategory,
  type TopTenOrder,
  type TopTenType,
  type TopTenVariant,
  type TrackItem,
} from '../../types/label-perf';
import { formatNumberSuffix, getRelMonth } from '../../utils/format';
import { getLegendItems } from '../../utils/hist';
import { getTrendDirection } from '../../utils/trend';
import {
  Card,
  Chart,
  ErrorDisplay,
  LegendBar,
  LegendList,
  SearchBar,
  Tabs,
  Title,
  TopTen,
  Trend,
  Value,
} from './components';
import { NoAccess } from './components/NoAccess';
import { SelectedLabelsCounter } from './components/SelectedLabelsCounter';

export function LabelPerf() {
  const user = useAmplifyUser();
  const vnextFlagFromUser = user.user?.feature_flags?.['dsc-exec-view-vnext'];
  const isDscExecViewVnextTrue =
    vnextFlagFromUser !== undefined
      ? vnextFlagFromUser
      : localStorage.getItem('dsc-exec-view-vnext') === 'true';

  const { t } = useTranslation('label-perf');
  const { trackEvent } = useAnalytics('Label Performance');

  const [chartTab, setChartTab] = useState<HistRange>(DEFAULT_CHART_RANGE);
  const [topTenTab, setTopTenTab] =
    useState<TopTenVariant>(DEFAULT_TOP_TEN_TAB);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInputValue, setSearchInputValue] = useState('');

  const [labels, setLabels] = useState<string[]>(
    searchParams
      .get('labels')
      ?.split(',')
      ?.filter((l) => isNaN(+l)) || [],
  );

  const renderLoadingState = (): JSX.Element => (
    <p>
      <Icon type="spinner" variant="solid" size="lg" className="fa-spin" />
    </p>
  );

  const updateSearchParams = useCallback(
    (selectedLabels: string[] | number[]) => {
      const params = new URLSearchParams('');

      if (selectedLabels && selectedLabels.length) {
        params.set('labels', selectedLabels.toString());
      }

      setSearchParams(params);
    },
    [setSearchParams],
  );

  // START OF Version-2 LABELS
  const [labelIds, setLabelIds] = useState<number[]>(
    searchParams
      .get('labels')
      ?.split(',')
      ?.map(Number)
      .filter((l) => !!l) || [],
  );

  const isDscExecViewLabelsFilterEnabled = useMemo(() => {
    const trimLabelsFlagFromUser =
      user.user?.feature_flags?.['dcs-exec-view-trim-labels'];

    let isTrimLabelsEnabled;

    if (trimLabelsFlagFromUser !== undefined) {
      isTrimLabelsEnabled = trimLabelsFlagFromUser;
    } else {
      const localStorageValue = localStorage.getItem(
        'dcs-exec-view-trim-labels',
      );

      isTrimLabelsEnabled = ['true', null].includes(localStorageValue);
    }

    if (!isTrimLabelsEnabled) {
      return false;
    }

    const userHasAllowedRole = !user.isLoading && user.user?.role === 'user';

    return userHasAllowedRole;
  }, [user]);

  // These are the full LabelData objects filtered by the current labelIds.
  const [filteredLabels, setFilteredLabels] = useState<LabelData[]>([]);

  const appliedFilterItems = useMemo<AppliedLabelFilter[]>(() => {
    return filteredLabels.map((label) => {
      const { labelId, name, updatedAt, updatedBy } = label;

      return {
        labelId,
        displayName: name,
        updatedAt,
        updatedBy,
      };
    });
  }, [filteredLabels]);

  const onSubmit: SubmitHandler<LabelPerfFilters> = useCallback(
    (formData) => {
      const selectedLabels = formData.labels.map((item) => item.labelId);

      setTopTenTab(DEFAULT_TOP_TEN_TAB);
      setLabelIds(selectedLabels);
      updateSearchParams(selectedLabels);
      trackEvent('labelPerformanceLabelFilterApplied', {
        label_count: formData.labels.length,
        labels_selected: formData.labels.map((l) => l.name),
      });
    },
    [setLabelIds, updateSearchParams, trackEvent],
  );

  const handleClearFilters = useCallback(() => {
    setFilteredLabels(DEFAULT_LABELS_FILTERS.labels);
    setLabelIds([]);
    updateSearchParams([]);
  }, [updateSearchParams]);

  const handleRemoveLabelFilter = useCallback(
    (deletedLabelId: number) => {
      const newFilteredLabelData = filteredLabels.filter(
        ({ labelId }) => labelId !== deletedLabelId,
      );

      setFilteredLabels(newFilteredLabelData);
      setLabelIds(newFilteredLabelData.map((l) => l.labelId));
      updateSearchParams(newFilteredLabelData.map((l) => l.labelId));
    },
    [filteredLabels, updateSearchParams],
  );

  const currentFilterValues: LabelPerfFilters = useMemo(() => {
    return {
      labels: filteredLabels.map((label) => ({
        labelId: label.labelId,
        name: label.name,
      })),
      isCrossover: false,
      isOnRoster: false,
    };
  }, [filteredLabels]);

  const methods = useForm<LabelPerfFilters>({
    defaultValues: structuredClone(currentFilterValues),
  });

  const {
    handleSubmit,
    watch,
    formState: { isDirty },
    reset,
  } = methods;
  const watchedLabels = watch('labels');

  const { data: labelData } = useQuery({
    queryKey: ['labels-v2'],
    queryFn: () => getLabels({}),
    enabled: isDscExecViewVnextTrue,
  });

  const { data: wmg1Labels } = useQuery({
    queryKey: ['labels-wmg'],
    queryFn: () => getWMG1Labels(),
    enabled: !!isDscExecViewLabelsFilterEnabled,
  });

  const { data: labelHierarchyData, isLoading: isLoadingLabelsHierarchy } =
    useQuery({
      queryKey: ['labels-hierarchies'],
      queryFn: () => getLabelHierarchy(),
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      enabled:
        isDscExecViewVnextTrue ||
        (!!wmg1Labels && !!isDscExecViewLabelsFilterEnabled),
    });

  const handleApplyOnClose = useCallback(() => {
    if (isDirty) {
      handleSubmit(onSubmit)();
    }
  }, [isDirty, handleSubmit, onSubmit]);

  const isApplyButtonDisabled = useMemo(() => {
    // Disable if no labels are selected in the form
    if (!watchedLabels || watchedLabels.length === 0) {
      return true;
    }

    return !isDirty;
  }, [watchedLabels, isDirty]);

  const filteredLabelHierarchyItems = useMemo(() => {
    // If the label filter is disabled, return all hierarchy items without filtering
    if (!isDscExecViewLabelsFilterEnabled) {
      return labelHierarchyData?.items || [];
    }

    if (!labelHierarchyData?.items || !wmg1Labels?.length) {
      return [];
    }

    const wmg1LabelIds = new Set(wmg1Labels);

    const isLabelInWMG1Hierarchy = (
      label: LabelHierarchy,
      wmg1Set: Set<number>,
    ): boolean => {
      // Check if the current label's ID is in the WMG1 set
      if (wmg1Set.has(label.label_id)) {
        return true;
      }
      // If not, recursively check its children

      if (label.child_labels && label.child_labels.length > 0) {
        return label.child_labels.some((child) =>
          isLabelInWMG1Hierarchy(child, wmg1Set),
        );
      }

      return false;
    };

    // This function will recursively filter the hierarchy,
    // only including nodes that have themselves or any descendant in wmg1LabelIds
    const deepFilterHierarchy = (
      hierarchy: LabelHierarchy[],
      wmg1Set: Set<number>,
    ): LabelHierarchy[] => {
      return hierarchy
        .filter((item) => isLabelInWMG1Hierarchy(item, wmg1Set)) // Filter based on the recursive check
        .map((item) => ({
          ...item,
          // Recursively apply deepFilterHierarchy to child_labels
          child_labels: item.child_labels
            ? deepFilterHierarchy(item.child_labels, wmg1Set)
            : [],
        }));
    };

    return deepFilterHierarchy(labelHierarchyData.items, wmg1LabelIds);
  }, [labelHierarchyData?.items, wmg1Labels, isDscExecViewLabelsFilterEnabled]);

  // Update the filtered label data once the data loads.
  useEffect(() => {
    if (labelIds && labelData) {
      const filteredData = labelData.filter((label: LabelData) =>
        labelIds.includes(label.labelId),
      );

      setFilteredLabels(filteredData);
    }
  }, [labelData, labelIds]);

  const filteredTotalCount = useMemo(() => {
    if (!labelData || !filteredLabelHierarchyItems) {
      return 0;
    }

    const parentLabelIds = getAllParentLabelIds(filteredLabelHierarchyItems);

    if (isDscExecViewLabelsFilterEnabled) {
      return getAllChildLabelIds(filteredLabelHierarchyItems).size;
    }

    return labelData.filter(
      (label: LabelData) => !parentLabelIds.has(label.labelId),
    ).length;
  }, [
    labelData,
    filteredLabelHierarchyItems,
    isDscExecViewLabelsFilterEnabled,
  ]);

  // END OF V2

  const {
    data: labelsData,
    isLoading: labesDataLoading,
    error: labelsError,
  } = useQuery({
    queryKey: ['labels', searchInputValue],
    queryFn: () => getLables('major_label', searchInputValue),
    enabled: !isDscExecViewVnextTrue,
  });

  const initialLabelsCount = useRef<number | null>(null);

  /* 
    When displaying the total count of labels in the search bar, we want the number
    to be the initial number of labels that were found without filtering the results. 
  */
  useEffect(() => {
    if (
      searchInputValue === '' &&
      labelsData?.length !== undefined &&
      initialLabelsCount.current === null
    ) {
      initialLabelsCount.current = labelsData.length;
    }
  }, [labelsData?.length, searchInputValue]);

  const {
    data: labelPerfData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['label-perf', labels, labelIds],
    queryFn: () => getLabelPerfReport(labels, labelIds),
  });

  const { data: monthlyContrib } = useQuery({
    queryKey: ['monthly-contrib', labels, labelIds],
    queryFn: () => getMonthlyContrib(labels, 5 * 12, labelIds),
  });

  const {
    data: chartData,
    isLoading: chartLoading,
    isError: chartError,
  } = useQuery({
    queryKey: ['fixes-history', labels, chartTab, labelIds],
    queryFn: ({ queryKey }) => {
      const [, , histRange] = queryKey;

      return getFixesHistory(labels, histRange as HistRange, labelIds);
    },
  });

  const {
    data: topTenData,
    isLoading: topTenDataLoading,
    isError: topTenDataError,
  } = useQuery({
    queryKey: ['top-ten', labels, topTenTab, labelIds],
    queryFn: () => {
      const metric: TopTenType = topTenTab.includes('revenue')
        ? 'revenue'
        : 'streams';
      const order: TopTenOrder = topTenTab.includes('Up') ? 'top' : 'bottom';

      return getTopTenTracks(metric, order, labels, labelIds);
    },
  });

  const onSearchInputClear = useCallback(() => {
    setSearchInputValue('');
  }, []);

  const onSubmitLabels = useCallback(
    (selectedLabels: string[]) => {
      setTopTenTab(DEFAULT_TOP_TEN_TAB);
      setLabels(selectedLabels);
      updateSearchParams(selectedLabels);

      trackEvent('labelPerformanceLabelFilterApplied', {
        label_count: selectedLabels.length,
        labels_selected: selectedLabels,
      });
    },
    [updateSearchParams, trackEvent],
  );

  useEffect(() => {
    if (labelPerfData) {
      // Reset the form with the *current* filteredLabels to mark it as not dirty
      // with respect to the currently displayed data.
      reset({
        labels: filteredLabels.map((label) => ({
          labelId: label.labelId,
          name: label.name,
        })),
        isCrossover: false,
        isOnRoster: false,
      });
    }
  }, [labelPerfData, filteredLabels, reset]);

  const renderPerformanceCards = (data: LabelPerfModel): JSX.Element => (
    <div className="grid grid-cols-3 gap-md">
      <Card className="bg-level-03">
        <Title className="mb-auto" data-testid="total-assets-title">
          {t('body.totalAssets')}
        </Title>
        <Value className="!text-display-5xl">
          {formatNumberSuffix(data.totalAssets)}
        </Value>
      </Card>

      <Card>
        <Title className="mb-md text-subtle">
          {t('body.overallMonthlyStreams')}
        </Title>
        <div>
          <Trend
            direction={getTrendDirection(data.overallMonthlyStreamsChange)}
          >
            {formatNumberSuffix(data.overallMonthlyStreamsChange, 1, false)}
          </Trend>
          <span className="text-[18px] font-normal">
            {t('body.monthComparison', {
              first: getRelMonth(-1),
              second: getRelMonth(-2),
            })}
          </span>
          <Value className="mt-sm">
            {formatNumberSuffix(data.overallMonthlyStreams)}
          </Value>
        </div>
      </Card>

      <Card>
        <Title className="mb-md text-subtle">
          {t('body.overallMonthlyRevenue')}
        </Title>
        <div>
          <Trend
            direction={getTrendDirection(data.overallMonthlyRevenueChange)}
          >
            {'$' +
              formatNumberSuffix(data.overallMonthlyRevenueChange, 1, false)}
          </Trend>
          <span className="text-[18px] font-normal">
            {t('body.monthComparison', {
              first: getRelMonth(-2),
              second: getRelMonth(-3),
            })}
          </span>
          <Value className="mt-sm">
            ${formatNumberSuffix(data.overallMonthlyRevenue)}
            <span>
              {' '}
              {t('in')} {getRelMonth(-2)}
            </span>
          </Value>
        </div>
      </Card>
    </div>
  );

  const renderOpportunityCards = (
    data: LabelPerfModel,
    monthlyContributions: MonthlyContrib[],
  ): JSX.Element => (
    <div className="grid grid-cols-3 gap-md">
      <Card>
        <Title className="text-white">
          {t('body.yearlyRevenueOpportunity')}
          <Icon
            type="info-circle"
            variant="solid"
            size="xs"
            className="ml-xs"
          />
        </Title>
        <Value className="mt-auto">
          <span className="text-display-5xl leading-none">
            ${formatNumberSuffix(data.opportunityRevenueFromFixes)}
          </span>
          <span className="text-display-md text-subtle">/{t('date.year')}</span>
        </Value>
      </Card>

      <Card>
        <div>
          <Title className="mb-md text-subtle">
            {t('body.allStreamsFromFixes')}
            <Icon
              type="info-circle"
              variant="solid"
              size="xs"
              className="ml-xs"
            />
          </Title>
          <Value>
            {monthlyContributions.length
              ? formatNumberSuffix(
                  monthlyContributions.reduce((r, v) => r + v.totalStreams, 0),
                  0,
                )
              : '--'}
          </Value>
        </div>

        <Separator className="my-lg" />

        <Title className="mb-md text-subtle">
          {t('body.monthlyStreamsIncrement')}
          <Icon
            type="info-circle"
            variant="solid"
            size="xs"
            className="ml-xs"
          />
        </Title>
        <div>
          <Trend direction={getTrendDirection(data.streamsFromFixesChange)}>
            {formatNumberSuffix(data.streamsFromFixesChange, 1, false)}
          </Trend>
          <span className="text-[18px] font-normal">
            {t('body.monthComparison', {
              first: getRelMonth(-1),
              second: getRelMonth(-2),
            })}
          </span>
          <Value className="mt-md">
            {formatNumberSuffix(data.streamsFromFixes)}
          </Value>
        </div>
      </Card>

      <Card>
        <div>
          <Title className="mb-md text-subtle">
            {t('body.allRevenueFromFixes')}
            <Icon
              type="info-circle"
              variant="solid"
              size="xs"
              className="ml-xs"
            />
          </Title>
          <Value className="text-success mt-md">
            {monthlyContributions.length
              ? `$${formatNumberSuffix(
                  monthlyContributions.reduce((r, v) => r + v.totalRevenue, 0),
                  0,
                )}`
              : '--'}
          </Value>
        </div>
        <Separator className="my-lg" />
        <Title className="mb-md text-subtle">
          {t('body.monthlyRevenueIncrement')}
          <Icon
            type="info-circle"
            variant="solid"
            size="xs"
            className="ml-xs"
          />
        </Title>
        <div>
          <Trend direction={getTrendDirection(data.revenueFromFixesChange)}>
            {'$' + formatNumberSuffix(data.revenueFromFixesChange, 1, false)}
          </Trend>
          <span className="text-[18px] font-normal">
            {t('body.monthComparison', {
              first: getRelMonth(-1),
              second: getRelMonth(-2),
            })}
          </span>
          <Value className="mt-md">
            ${formatNumberSuffix(data.revenueFromFixes)}
          </Value>
        </div>
      </Card>
    </div>
  );

  const renderChartsData = (
    data: LabelPerfModel,
    chartsData: FixesHistValue[] | undefined,
  ): JSX.Element => (
    <div className="mt-lg rounded-sm border px-2xl py-lg bg-level-02 border-emphasis-subtle">
      {/* HEADER */}
      <div className="mb-md flex justify-between">
        <p className="text-body-sm font-bold">
          {t('body.needsReviewAccurate')}
        </p>
        <Tabs
          defaultTab={chartTab}
          items={CHART_TABS}
          loading={chartLoading}
          onChange={(tabItem) => {
            const newChartTab = tabItem.id as HistRange;

            trackEvent(
              'labelPerformanceNeedsReviewVSAccuracyTableDateFilterChange',
              {
                previous_date_filter: chartTab,
                selected_date_filter: newChartTab,
              },
            );

            setChartTab(newChartTab);
          }}
          theme="toggler"
        />
      </div>
      {/* INFO */}
      <div className="mb-md grid grid-cols-2 gap-4xl">
        <div>
          <div className="flex">
            <div>
              <p className="text-body-md font-bold text-subtle">
                {t('body.needsReview')}
                <Icon
                  type="magnifying-glass"
                  variant="solid"
                  size="sm"
                  className="ml-sm"
                />
              </p>
              <p>
                <span className="text-display-lg">
                  {formatNumberSuffix(data.totalGaps)}
                </span>
                <span className="ml-sm text-display-sm text-subtle">
                  ({((data.totalGaps / data.totalAssets) * 100).toFixed(2)}%)
                </span>
                <span className="text-subtle">
                  {t('body.ofTotalAssets', {
                    totalAssets: formatNumberSuffix(data.totalAssets),
                  })}
                </span>
              </p>
            </div>
            <div className="ml-auto">
              {chartsData ? (
                <LegendList items={getLegendItems(chartData)} />
              ) : null}
            </div>
          </div>
          {chartsData ? (
            <LegendBar className="mt-sm" items={getLegendItems(chartData)} />
          ) : null}
        </div>
        <div />
      </div>
      {/* CHART CANVAS */}
      <div className="relative">
        <Chart
          className="-ml-md"
          data={chartsData || []}
          isLoading={chartLoading}
          isError={chartError}
        />
      </div>
    </div>
  );

  const renderTopTenTable = (
    data: TrackItem[] | undefined,
    isLoadingTopTen: boolean,
    isErrorTopTen: boolean,
  ): JSX.Element => (
    <div className="mt-lg">
      <Tabs
        items={[
          { id: TopTenCategory.StreamsUp, name: t('body.topTenStreamsGrowth') },
          { id: TopTenCategory.StreamsDown, name: t('body.topTenStreamsLoss') },
          { id: TopTenCategory.RevenueUp, name: t('body.topTenRevenueGrowth') },
          { id: TopTenCategory.RevenueDown, name: t('body.topTenRevenueLoss') },
        ]}
        onChange={(tab) => {
          const newTopTenTab = tab.id as TopTenVariant;

          trackEvent('labelPerformanceOverallPerformanceTableTabChange', {
            previous_tab: topTenTab,
            selected_tab: newTopTenTab,
          });

          setTopTenTab(newTopTenTab);
        }}
        theme="default"
      />
      <TopTen
        data={data}
        type={topTenTab}
        isLoading={isLoadingTopTen}
        isError={isErrorTopTen}
      />
    </div>
  );

  const isForbidden =
    labelsError instanceof APIError && labelsError.status === 403;
  const noAccess =
    (!searchInputValue && labelsData?.length === 0 && !labesDataLoading) ||
    isForbidden;

  if (noAccess) {
    return <NoAccess />;
  }

  return (
    <>
      <Helmet>
        <title>{t('header.title')}</title>
      </Helmet>
      <div>
        <Header />
        <Separator className="mb-lg mt-md" />
      </div>
      {user.user ? (
        <div className="max-w-[86em]">
          {!isDscExecViewVnextTrue ? (
            <div>
              <Title className="mb-sm text-body-md text-default">
                {t('body.majorLabels')}
              </Title>
              <SearchBar
                data={labelsData || []}
                totalLabelsCount={initialLabelsCount.current || 0}
                isLoading={labesDataLoading}
                inputValue={searchInputValue}
                selectedValues={labels}
                placeholder={t('body.searchLabels')}
                onSubmit={onSubmitLabels}
                onChange={setSearchInputValue}
                onClear={onSearchInputClear}
                aria-label="Search labels by label name"
              />
            </div>
          ) : (
            <section className="mb-2xl flex flex-col gap-sm">
              <FormProvider {...methods}>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="flex basis-full flex-col"
                >
                  <div className="mb-sm flex justify-between">
                    <p className="font-semibold">{t('body.labels')}</p>
                    {labelData ? (
                      <SelectedLabelsCounter
                        selectedCount={labelIds.length}
                        totalCount={filteredTotalCount}
                        label={t('body.selected')}
                      />
                    ) : null}
                  </div>
                  <div className="flex gap-md">
                    <LabelFilter
                      originalLabels={currentFilterValues.labels}
                      labelHierarchyData={filteredLabelHierarchyItems}
                      isLoading={isLoadingLabelsHierarchy}
                      disabled={isLoading}
                      onApply={handleApplyOnClose}
                    />
                    <Button
                      variant={isApplyButtonDisabled ? 'secondary' : 'primary'}
                      disabled={isApplyButtonDisabled || isLoading}
                      size="large"
                      type="submit"
                    >
                      {t('actions.apply')}
                    </Button>
                  </div>
                </form>
              </FormProvider>

              {labelIds.length ? (
                <AppliedFilterList
                  isCrossover={false}
                  labelFilters={appliedFilterItems}
                  // @TODO WMG1-572 - Put back as a required prop once back end data is fixed.
                  // onRemoveCrossover={() => {}}
                  onRemoveOnRoster={() => {}}
                  onRemoveLabel={handleRemoveLabelFilter}
                  onClear={handleClearFilters}
                />
              ) : null}
            </section>
          )}

          {isLoading ? renderLoadingState() : null}
          {isError && !labesDataLoading ? (
            <ErrorDisplay
              title={t('error.failedLoading')}
              description={t('error.refresh')}
              className="min-h-[20em]"
            />
          ) : null}

          {labelPerfData ? (
            <div>
              {/* Header Section */}
              <div className="mb-md flex items-end justify-between">
                <Title className="text-body-lg text-default">
                  {t('body.overallPerformance')}
                </Title>
                <p className="text-body-md">
                  <Icon type="info-circle" variant="solid" size="sm" />
                  <span className="ml-xs">
                    {t('body.streamCountInfo')}{' '}
                    <span className="underline">{t('estimated')}</span>.
                  </span>
                </p>
              </div>

              {/* Performance Cards */}
              <div className="mb-2xl overflow-hidden">
                {renderPerformanceCards(labelPerfData)}
              </div>

              {/* TOP10 */}
              {renderTopTenTable(
                topTenData,
                topTenDataLoading,
                topTenDataError,
              )}

              {/* Opportunity Section */}
              <Title className="mb-md mt-2xl text-body-lg text-default">
                {t('body.territoryDSPRevenueOpportunities')}
              </Title>

              <div className="overflow-hidden">
                {renderOpportunityCards(labelPerfData, monthlyContrib || [])}
              </div>

              {/* CHART */}
              {renderChartsData(labelPerfData, chartData)}
            </div>
          ) : null}
        </div>
      ) : (
        renderLoadingState()
      )}
    </>
  );
}

LabelPerf.displayName = 'LabelPerf';
